import axios from "axios";
import { inject, injectable } from "inversify";
import { IJobRepository } from "@domain/repositories";
import { Job, PagedBatchList, JobPipelineStepDetail, RawFile, PagedJobList, PagedBatchRawFileList, AuditingFile, Step } from "@domain/schemas/BI";
import { CreateJob } from "@domain/schemas/BI";
import type { MergeNodePoolOption } from "@domain/valueObjects";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";

// Implementation of the IJobRepository interface
@injectable()
export class JobRepository implements IJobRepository {
  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async getJobByIDAsync(jobId: string): Promise<Job> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.jobById}`.replace("{job_id}", jobId);
    return this._requestHandlerService.handleRequestAsync<Job>(() => axios.get<Job>(url));
  }

  public async deleteJobByIDAsync(jobId: string): Promise<Job> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.jobById}`.replace("{job_id}", jobId);
    return this._requestHandlerService.handleRequestAsync<Job>(() => axios.delete<Job>(url));
  }

  public async cancelPendingBatchesByJobIDAsync(jobId: string): Promise<Job> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.cancelPendingBatchesByJobId}`.replace("{job_id}", jobId);
    return this._requestHandlerService.handleRequestAsync<Job>(() => axios.put<Job>(url));
  }

  public async abortJobByIDAsync(jobId: string): Promise<Job> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.abortJobById}`.replace("{job_id}", jobId);
    return this._requestHandlerService.handleRequestAsync<Job>(() => axios.put<Job>(url));
  }

  public async resumeJobByIDAsync(jobId: string): Promise<Job> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.resumeJobById}`.replace("{job_id}", jobId);
    return this._requestHandlerService.handleRequestAsync<Job>(() => axios.put<Job>(url));
  }

  public async getStepByIDAsync(jobPipelineStepId: string): Promise<Step> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.steps}`.replace("{job_pipeline_step_id}", jobPipelineStepId);
    return this._requestHandlerService.handleRequestAsync<Step>(() => axios.get<Step>(url));
  }

  public async getAvailableMergeNodePoolsAsync(): Promise<MergeNodePoolOption[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.availableMergeNodePools}`;
    return this._requestHandlerService.handleRequestAsync<MergeNodePoolOption[]>(() => axios.get<MergeNodePoolOption[]>(url));
  }

  public async getListAsync(page?: number, pageSize?: number, _sortBy?: string, _sortDirection?: any, name?: string, createdBy?: string): Promise<PagedJobList> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.jobs}`;
    const params = new URLSearchParams();

    if (page !== undefined) params.append("page", page.toString());
    if (pageSize !== undefined) params.append("pageSize", pageSize.toString());
    if (_sortBy !== undefined) params.append("sortBy", _sortBy);
    if (_sortDirection !== undefined) params.append("sortDirection", _sortDirection);
    if (name !== undefined) params.append("name", name);
    if (createdBy !== undefined) params.append("createdBy", createdBy);

    const queryString = params.toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    return this._requestHandlerService.handleRequestAsync<PagedJobList>(() => axios.get<PagedJobList>(fullUrl));
  }

  public async getJobStepListAsync(jobId: string): Promise<JobPipelineStepDetail[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.jobSteps}`.replace("{job_id}", jobId);
    return this._requestHandlerService.handleRequestAsync<JobPipelineStepDetail[]>(() => axios.get(url));
  }

  public async getJobBatchSampleListAsync(jobId: string, jobPipelineStepId: string, batchId: string): Promise<PagedBatchRawFileList> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.stepBatchSamples}`.replace("{job_id}", jobId).replace("{job_pipeline_step_id}", jobPipelineStepId).replace("{batch_id}", batchId);
    return this._requestHandlerService.handleRequestAsync(() => axios.get(url));
  }

  public async startAuditLogs(jobId: string, jobPipelineStepId: string, batchId: string, sessionId: string): Promise<AuditingFile> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.batchLogs}`
      .replace("{job_id}", jobId)
      .replace("{job_pipeline_step_id}", jobPipelineStepId)
      .replace("{batch_id}", batchId)
      .replace("{session_id}", sessionId);
    return this._requestHandlerService.handleRequestAsync(() => axios.get(url));
  }

  public async stopAuditLogs(jobId: string, jobPipelineStepId: string, batchId: string, sessionId: string): Promise<AuditingFile> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.batchLogs}`
      .replace("{job_id}", jobId)
      .replace("{job_pipeline_step_id}", jobPipelineStepId)
      .replace("{batch_id}", batchId)
      .replace("{session_id}", sessionId);
    return this._requestHandlerService.handleRequestAsync(() => axios.post(url));
  }

  public async getResults(jobId: string, jobPipelineStepId: string, batchId: string, sessionId: string): Promise<AuditingFile> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.batchResults}`
      .replace("{job_id}", jobId)
      .replace("{job_pipeline_step_id}", jobPipelineStepId)
      .replace("{batch_id}", batchId)
      .replace("{session_id}", sessionId);
    return this._requestHandlerService.handleRequestAsync(() => axios.get(url));
  }

  public async getJobStepBatchListAsync(jobId: string, jobPipelineStepId: string): Promise<PagedBatchList> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.stepBatches}`.replace("{job_id}", jobId).replace("{job_pipeline_step_id}", jobPipelineStepId);
    return this._requestHandlerService.handleRequestAsync(() => axios.get(url));
  }

  public async getSampleListAsync(sampleIds: string[]): Promise<RawFile[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFilesBatch}`.replace("{ids}", sampleIds.join(","));
    return this._requestHandlerService.handleRequestAsync(() => axios.get(url));
  }

  public async createAsync(createJob: CreateJob): Promise<Job> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.jobs}`;
    const job = await this._requestHandlerService.handleRequestAsync<Job>(() => axios.post(url, createJob));

    if (!job.id || job.id.trim() === "") {
      throw new Error("The response ID is null, undefined, or empty.");
    }

    return job;
  }
}
